import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../components/AnimatedSection';
import { MagneticButton } from '../components/MagneticButton';
import { ArrowRight } from 'lucide-react';

const services = [
  {
    num: '01',
    title: 'Brand Strategy & Positioning',
    tagline: 'Define who you are and why it matters.',
    desc: 'We don\'t start with visuals. We start with understanding. We identify your market position, clarify your messaging, and build a foundation that makes your brand self-explanatory — even when you\'re not in the room.',
    deliverables: ['Brand Audit & Discovery', 'Positioning Strategy', 'Messaging Architecture', 'Brand Voice & Tone'],
  },
  {
    num: '02',
    title: 'Logo & Visual Identity',
    tagline: 'Create systems that scale.',
    desc: 'A logo is not a brand. We design comprehensive visual identity systems — mark, typography, color, motion — that work across every touchpoint with consistency and intention.',
    deliverables: ['Logo Design', 'Color & Typography Systems', 'Brand Guidelines', 'Visual Identity Rollout'],
  },
  {
    num: '03',
    title: 'Packaging & Physical Design',
    tagline: 'Tangible brand experiences.',
    desc: 'Great packaging doesn\'t just contain a product — it contains a promise. We design packaging that works as a silent salesperson, communicating your brand\'s value instantly on shelf or in hand.',
    deliverables: ['Structural Design', 'Label & Surface Design', 'Retail Shelf Strategy', 'Print-Ready Artwork'],
  },
  {
    num: '04',
    title: 'Product UI/UX Design',
    tagline: 'Intuitive digital products.',
    desc: 'From zero-to-one product strategy to feature expansion — we partner with product teams to design digital products that are clear, purposeful, and built for real users.',
    deliverables: ['User Research & Testing', 'Wireframes & Prototypes', 'High-Fidelity UI Design', 'Design System Creation'],
  },
  {
    num: '05',
    title: 'Content & Communication Design',
    tagline: 'Clear messaging across all channels.',
    desc: 'Every customer touchpoint is a communication opportunity. We design pitch decks, business presentations, and marketing collateral that communicate your value without confusion.',
    deliverables: ['Business & Pitch Decks', 'Marketing Collateral', 'Presentation Design', 'Brand Copywriting'],
  },
  {
    num: '06',
    title: 'Website & Digital Experience',
    tagline: 'Seamless online presence.',
    desc: 'Your website should be your best salesperson. We design digital experiences that communicate clearly, build trust instantly, and convert visitors into clients.',
    deliverables: ['Website Design', 'Landing Pages', 'UX Audits', 'Developer Handoff'],
  },
  {
    num: '07',
    title: '2D Animation & Video Creation',
    tagline: 'Motion that communicates.',
    desc: 'We create compelling 2D animated videos and promotional content that brings your brand story to life — for ads, explainers, product launches, and social campaigns.',
    deliverables: ['2D Animated Explainers', 'Promotional Videos', 'Brand Storytelling', 'Motion Graphics'],
  },
  {
    num: '08',
    title: 'Instagram Reels & Content Strategy',
    tagline: 'Scroll-stopping content, consistently.',
    desc: 'We build your Instagram presence with a strategic content calendar, high-performing Reels, and a visual identity that\'s unmistakably yours — helping you grow, engage, and convert.',
    deliverables: ['Content Calendar & Strategy', 'Reels Production', 'Caption & Hashtag Strategy', 'Analytics & Reporting'],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" style={{ paddingTop: 64, background: 'var(--canvas)' }}>
      {/* Hero */}
      <section style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--border)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="container-site">
          <AnimatedSection>
            <span className="label-sm">What We Do</span>
            <h1 className="heading-xl" style={{ fontSize: 'clamp(48px, 8vw, 100px)', marginTop: 20 }}>
              We build clarity,<br />
              <span style={{ color: 'var(--accent)' }}>not decoration.</span>
            </h1>
            <p className="body-base" style={{ maxWidth: 560, marginTop: 24, fontSize: 16 }}>
              Outbox Thinking helps brands explain themselves clearly — across digital, physical, and business touchpoints. Everything works as one system, not isolated outputs.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Services list */}
      <section data-testid="services-list">
        <div className="container-site">
          {services.map((service, i) => (
            <AnimatedSection key={i} delay={0.05}>
              <div
                data-testid={`service-item-${i}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '48px 1fr 1fr',
                  gap: '0 40px',
                  padding: '56px 0',
                  borderBottom: '1px solid var(--border)',
                  alignItems: 'start',
                }}
                className="services-row"
              >
                {/* Number */}
                <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-300)', letterSpacing: '0.1em', paddingTop: 4 }}>{service.num}</span>

                {/* Title */}
                <div>
                  <h2 className="heading-md" style={{ fontSize: 'clamp(20px, 2.5vw, 30px)', marginBottom: 6 }}>
                    {service.title}
                  </h2>
                  <p className="font-mono" style={{ fontSize: 11, color: 'var(--accent)', letterSpacing: '0.06em' }}>
                    {service.tagline}
                  </p>
                </div>

                {/* Desc + deliverables */}
                <div>
                  <p className="body-base" style={{ marginBottom: 24, fontSize: 14 }}>{service.desc}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px' }}>
                    {service.deliverables.map((d, j) => (
                      <motion.div
                        key={j}
                        initial={{ opacity: 0, x: -6 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: j * 0.06 }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--ink-500)' }}>{d}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Process block from deck */}
      <section style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="container-site">
          <AnimatedSection style={{ marginBottom: 56 }}>
            <span className="label-sm">How We Work</span>
            <h2 className="heading-lg" style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginTop: 12 }}>
              Simple. Structured. Intentional.
            </h2>
          </AnimatedSection>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {[
              { num: '01', title: 'Observe',   desc: 'We understand the business, market, and audience.', tags: 'Research · Discovery · Insights' },
              { num: '02', title: 'Define',    desc: 'We clarify what the brand should say and how.', tags: 'Strategy · Positioning · Messaging' },
              { num: '03', title: 'Structure', desc: 'We build systems — visual, digital, and narrative.', tags: 'Design Systems · Guidelines · Frameworks' },
              { num: '04', title: 'Apply',     desc: 'We implement consistently across all touchpoints.', tags: 'Execution · Rollout · Support' },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                style={{ background: 'var(--canvas)', padding: '36px 28px' }}
              >
                <div style={{ fontSize: 56, fontFamily: 'Space Grotesk', fontWeight: 700, color: 'var(--ink-100)', lineHeight: 1, marginBottom: 16 }}>
                  {step.num}
                </div>
                <h3 className="heading-md" style={{ fontSize: 18, marginBottom: 8 }}>{step.title}</h3>
                <p className="body-base" style={{ fontSize: 13, marginBottom: 12 }}>{step.desc}</p>
                <p className="font-mono" style={{ fontSize: 10, color: 'var(--accent)', letterSpacing: '0.06em' }}>{step.tags}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="services-cta" style={{ background: 'var(--ink-950)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="container-site" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 }}>
          <AnimatedSection>
            <span className="label-sm" style={{ color: 'var(--ink-500)' }}>Ready to Build Clarity?</span>
            <h2 className="heading-lg" style={{ fontSize: 'clamp(28px, 4vw, 48px)', color: 'var(--canvas)', marginTop: 12 }}>
              If this made sense,<br />we should probably talk.
            </h2>
          </AnimatedSection>
          <Link to="/contact" style={{ textDecoration: 'none' }} data-testid="services-cta-btn">
            <MagneticButton
              style={{ background: 'var(--canvas)', color: 'var(--ink-950)', border: 'none', padding: '13px 28px', borderRadius: 6, fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
              data-testid="services-contact-btn"
            >
              Start a Project <ArrowRight size={14} />
            </MagneticButton>
          </Link>
        </div>
      </section>
    </div>
  );
}
